/**
 * **Everything `shared/ui/PlanCard` says, in the trainer's language.**
 *
 * The card itself takes finished strings, and that is the right decision for the reason its own
 * header gives: `shared/` has no business vocabulary and no `t`, so it draws a plan without knowing
 * what a plan is. What that decision left behind is this module's reason to exist — the DRAWING was
 * unified on 1 September 2026 and the WORDS were not, so the client detail's summary column and the
 * roster's preview panel each composed the same six strings from the same keys, separately.
 *
 * That is the half the two had actually drifted in. All four differences the promotion fixed were
 * words, not pixels: an untranslated `Strength` on the chip, a missing scheduling line, a
 * lower-cased week counter, and a horizon named by its date instead of by its distance. Sharing the
 * layer where there was no problem and copying the layer where there was is the shape this fixes.
 *
 * ── Why `entities/client` ─────────────────────────────────────────────────────────────────────
 * Two consumers in the same layer, and a page may not import another page — the same argument that
 * put `planCalendarLink` here, about the same card. Where the card LEADS and what the card SAYS are
 * the two things both surfaces have to agree on, and they now live side by side.
 *
 * ── What it does NOT decide ───────────────────────────────────────────────────────────────────
 * `categoryKey` (a colour, not a word), `progressPct` (the two reach it by different routes: the
 * detail divides its own week counters, the roster is handed a percentage) and `coverImageUrl`.
 * Those stay at the call sites, where the difference is real.
 *
 * Pure: no React, no DOM, no query hook, no i18next import. `t` arrives as a parameter.
 */

/** i18next's `t`, narrowed to what the plan card's six strings need. */
export type PlanWordsT = (
  key: string,
  options?: {
    readonly defaultValue?: string;
    readonly count?: number;
    readonly done?: number;
    readonly total?: number;
    readonly mode?: string;
  },
) => string;

/** What both callers know about the plan, in the contract's own terms. */
export interface PlanCardWordsSource {
  /** `PlanTemplate.Category.ToString()` — `Strength`, `Hiit`, … — or `null`/empty for none. */
  readonly category: string | null;
  /** The plan runs at the client's own pace. It changes the unit AND the sentence — see below. */
  readonly selfPaced: boolean;
  /** Weeks on a calendar plan, CYCLES on a self-paced one. `null` where the read carries neither. */
  readonly weeksDone: number | null;
  readonly weeksTotal: number | null;
  /**
   * The only horizon the contract publishes. `null` for a plan with no end date; NEGATIVE for a plan
   * whose last week has passed while it is still Active.
   */
  readonly endsInDays: number | null;
}

/** The six strings `PlanCard` takes. Every one already written; the card decides none of them. */
export interface PlanCardWords {
  readonly eyebrow: string;
  readonly styleLine: string;
  readonly pausedLabel: string;
  /** `null` draws no chip at all, rather than an empty one. */
  readonly categoryLabel: string | null;
  readonly weeksLabel: string;
  /** `null` where the contract gives no horizon. The card then prints nothing there. */
  readonly endsLabel: string | null;
}

export function planCardWords(plan: PlanCardWordsSource, t: PlanWordsT): PlanCardWords {
  return {
    eyebrow: t('web:clientDetail.plan.eyebrow'),
    // The scheduling line. It exists on both cards because a trainer reading "Tydzień 4 z 8" and a
    // trainer reading "Trenował w 4 z 8 cykli" need to know which kind of plan they are looking at
    // before the numbers mean anything.
    styleLine: t('web:clientDetail.plan.style', {
      mode: t(
        plan.selfPaced ? 'web:clientDetail.plan.modeSelfPaced' : 'web:clientDetail.plan.modeCalendar',
      ),
    }),
    pausedLabel: t('web:clientDetail.plan.paused'),
    /*
     * The wire sends `PlanTemplate.Category.ToString()` — `Strength`, `Hiit`, `Flexibility` — and
     * the roster's chip printed it raw, so the one English word on a Polish screen sat on the plan
     * cover in capitals.
     *
     * The copied locale already names all six under `planTemplates.category.*`, which is what the
     * plan library's own chips read, so this is three surfaces agreeing rather than a new table. A
     * category this build has no word for keeps the server's spelling, which is the library's own
     * fallback.
     */
    categoryLabel:
      plan.category === null || plan.category === ''
        ? null
        : t(`planTemplates.category.${plan.category}`, { defaultValue: plan.category }),
    /*
     * The unit the plan actually COUNTS in. `weeksDone` / `weeksTotal` hold cycles on a self-paced
     * plan — the backend has no weeks to span there — so the week wording named a unit the plan
     * does not have.
     *
     * The self-paced key also says something DIFFERENT rather than the same thing in a new unit,
     * and that is not stylistic. `weeksDone` counts cycles the client has trained in; the standing
     * card beside it counts where the cursor STANDS. Both were rendered as "Cykl N z 3" with
     * different Ns on the same screen, which reads as one of them being wrong. Two questions, two
     * sentences.
     *
     * A read carrying neither figure says so instead of printing "z" with holes in it. Only the
     * roster's source can produce that today; the detail's projection fills both.
     *
     * A calendar plan with no trained week has no week to name. `weeksDone` is not a cursor: the
     * backend counts the distinct weeks carrying a FINISHED workout, so zero covers a plan that
     * starts next Monday and one that started a month ago and was never opened — and "Tydzień 0 z 5"
     * names a week that exists in neither. The contract cannot tell the two apart without redoing the
     * backend's week arithmetic, so the sentence says the one thing true of both. Self-paced keeps its
     * counter: "Trenował w 0 z 3 cykli" is already a true sentence about zero.
     */
    weeksLabel:
      plan.weeksDone === null || plan.weeksTotal === null
        ? t('web:clients.preview.planNoWeeks')
        : plan.weeksDone === 0 && !plan.selfPaced
          ? t('web:clientDetail.plan.notStarted', { count: plan.weeksTotal })
          : t(plan.selfPaced ? 'web:clientDetail.plan.cycles' : 'web:clientDetail.plan.weeks', {
              done: plan.weeksDone,
              total: plan.weeksTotal,
            }),
    endsLabel: plan.endsInDays == null ? null : horizonLabel(plan.endsInDays, t),
  };
}

/*
 * How far away the end is, in the only unit the contract publishes. A DATE would need a formatter
 * and a locale this module does not take.
 *
 * The backend sends a negative distance for a plan whose last week has passed while the assignment
 * is still Active — exactly the plan the attention queue raises as `PlanCompletionDue` ("Plan
 * skończył się …"), waiting for the trainer to close it. That plan read "jeszcze -17 dni". It now
 * says how long ago it ended, in the queue's own verb, and zero is today rather than "0 dni".
 */
function horizonLabel(endsInDays: number, t: PlanWordsT): string {
  if (endsInDays === 0) return t('web:clientDetail.plan.endsToday');
  if (endsInDays < 0) return t('web:clientDetail.plan.endedAgo', { count: -endsInDays });
  return t('web:clientDetail.plan.endsIn', { count: endsInDays });
}
